import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';

import { auth, db } from '../lib/firebase';
import { ensureJsAuthFromNative } from './authBridge';
import { createNotification, markCrewMessageNotificationsReadForThread } from './notifications';
import { getRelationshipDocId } from './users';
import type { UserProfile } from '../types';
import { NotificationType, View } from '../types';

const clean = (value: unknown) => String(value ?? '').trim();
const nowMs = () => Date.now();

export interface CrewDMThread {
  id: string;
  relationshipId: string;
  participantIds: string[];
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
  lastMessageText?: string;
  lastMessageSenderId?: string;
  lastMessageAt?: number;
  status: 'active' | string;
}

export interface CrewDMMessage {
  id: string;
  dmId: string;
  senderId: string;
  text: string;
  createdAt: number;
  updatedAt?: number;
  editedAt?: number;
  deletedAt?: number;
  status: 'visible' | 'deleted' | string;
}

export interface CrewDMInboxRow extends CrewDMThread {
  otherUid: string;
  lastReadAt: number;
  clearedAt: number;
  unreadCount: number;
  unread: boolean;
}

export interface CrewDMViewState {
  uid: string;
  clearedAt: number;
  updatedAt: number;
}

function toMillis(value: any, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value.toMillis === 'function') {
    try {
      return Number(value.toMillis()) || fallback;
    } catch {
      return fallback;
    }
  }
  if (value && typeof value.toDate === 'function') {
    try {
      return Number(value.toDate()?.getTime?.()) || fallback;
    } catch {
      return fallback;
    }
  }
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return fallback;
}

function normalizeThread(id: string, data: any): CrewDMThread {
  return {
    id,
    relationshipId: clean(data?.relationshipId) || id,
    participantIds: Array.isArray(data?.participantIds)
      ? data.participantIds.map((value: unknown) => clean(value)).filter(Boolean)
      : [],
    createdBy: clean(data?.createdBy) || undefined,
    createdAt: toMillis(data?.createdAt, Number(data?.createdAt || 0)),
    updatedAt: toMillis(data?.updatedAt, Number(data?.updatedAt || 0)),
    lastMessageText: clean(data?.lastMessageText) || undefined,
    lastMessageSenderId: clean(data?.lastMessageSenderId) || undefined,
    lastMessageAt: toMillis(data?.lastMessageAt, Number(data?.lastMessageAt || 0)) || undefined,
    status: clean(data?.status) || 'active',
  };
}

function normalizeMessage(id: string, data: any): CrewDMMessage {
  return {
    id,
    dmId: clean(data?.dmId),
    senderId: clean(data?.senderId),
    text: String(data?.text ?? ''),
    createdAt: toMillis(data?.createdAt, Number(data?.createdAt || 0)),
    updatedAt: toMillis(data?.updatedAt, Number(data?.updatedAt || 0)) || undefined,
    editedAt: toMillis(data?.editedAt, Number(data?.editedAt || 0)) || undefined,
    deletedAt: toMillis(data?.deletedAt, Number(data?.deletedAt || 0)) || undefined,
    status: clean(data?.status) || 'visible',
  };
}

async function ensureAuthed(label: string): Promise<string> {
  try {
    if (!auth.currentUser?.uid && Capacitor.isNativePlatform()) {
      await Promise.race([
        ensureJsAuthFromNative(),
        new Promise((resolve) => setTimeout(resolve, 2500)),
      ]);
    }
  } catch (error) {
    console.warn(`[crewDMs] auth bridge skipped for ${label}`, error);
  }

  const startedAt = Date.now();
  while (!auth.currentUser?.uid && Date.now() - startedAt < 2500) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  const uid = clean(auth.currentUser?.uid);
  if (!uid) throw new Error('You must be signed in to use Crew messages.');
  return uid;
}

async function requireAcceptedRelationship(currentUid: string, targetUid: string) {
  const dmId = getRelationshipDocId(currentUid, targetUid);
  const snap = await getDoc(doc(db, 'relationships', dmId));
  if (!snap.exists()) throw new Error('Crew relationship not found.');

  const data = snap.data() as any;
  const participantIds = Array.isArray(data?.participantIds)
    ? data.participantIds.map((value: unknown) => clean(value)).filter(Boolean)
    : [clean(data?.requesterId), clean(data?.recipientId)].filter(Boolean);

  if (
    clean(data?.status) !== 'accepted' ||
    participantIds.length !== 2 ||
    !participantIds.includes(currentUid) ||
    !participantIds.includes(targetUid)
  ) {
    throw new Error('Crew messaging is only available between accepted crewmates.');
  }

  return { dmId, participantIds: [...participantIds].sort() };
}

export function getCrewDMId(uidA: string, uidB: string): string {
  return getRelationshipDocId(uidA, uidB);
}

export function subscribeCrewDMThreadState(
  dmId: string,
  currentUid: string,
  onChange: (state: { exists: boolean; thread: CrewDMThread | null }) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const id = clean(dmId);
  const uid = clean(currentUid);

  if (!id || !uid) {
    onChange({ exists: false, thread: null });
    return () => {};
  }

  return onSnapshot(
    doc(db, 'crewDMs', id),
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange({ exists: false, thread: null });
        return;
      }

      const thread = normalizeThread(snapshot.id, snapshot.data());
      if (!thread.participantIds.includes(uid)) {
        onChange({ exists: false, thread: null });
        return;
      }

      onChange({ exists: true, thread });
    },
    (error) => {
      console.warn('[crewDMs] thread-state subscription failed', { dmId: id, error });
      onError?.(error);
      onChange({ exists: false, thread: null });
    },
  );
}

export function subscribeCrewDMInbox(
  uid: string,
  dmIds: string[],
  onChange: (rows: CrewDMInboxRow[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const currentUid = clean(uid);
  const desiredDmIds = Array.from(
    new Set((Array.isArray(dmIds) ? dmIds : []).map(clean).filter(Boolean)),
  );

  if (!currentUid || !desiredDmIds.length) {
    onChange([]);
    return () => {};
  }

  let cancelled = false;
  const threads = new Map<string, CrewDMThread>();
  const readAtByDm = new Map<string, number>();
  const clearedAtByDm = new Map<string, number>();
  const messageMetaByDm = new Map<
    string,
    Array<{ senderId: string; createdAt: number; status: string }>
  >();
  const threadUnsubs = new Map<string, Unsubscribe>();
  const readUnsubs = new Map<string, Unsubscribe>();
  const viewUnsubs = new Map<string, Unsubscribe>();
  const messageUnsubs = new Map<string, Unsubscribe>();

  const emit = () => {
    if (cancelled) return;
    const rows = Array.from(threads.values())
      .filter((thread) => {
        const clearedAt = clearedAtByDm.get(thread.id) || 0;
        const lastMessageAt = Number(thread.lastMessageAt || 0);
        return (
          thread.status !== 'deleted' &&
          thread.participantIds.includes(currentUid) &&
          (!clearedAt || lastMessageAt > clearedAt)
        );
      })
      .map((thread) => {
        const otherUid =
          thread.participantIds.find((id) => id !== currentUid) || '';
        const clearedAt = clearedAtByDm.get(thread.id) || 0;
        const lastReadAt = Math.max(readAtByDm.get(thread.id) || 0, clearedAt);
        const lastMessageAt = Number(thread.lastMessageAt || 0);
        const unreadCount = (messageMetaByDm.get(thread.id) || []).filter(
          (message) =>
            message.status !== 'deleted' &&
            message.senderId !== currentUid &&
            message.createdAt > lastReadAt,
        ).length;

        // Keep the old thread-level fallback while the message listener is warming
        // up so an unread badge never disappears briefly during subscription setup.
        const threadLooksUnread =
          Boolean(lastMessageAt) &&
          thread.lastMessageSenderId !== currentUid &&
          lastMessageAt > lastReadAt;

        return {
          ...thread,
          otherUid,
          lastReadAt,
          clearedAt,
          unreadCount: unreadCount || (threadLooksUnread ? 1 : 0),
          unread: unreadCount > 0 || threadLooksUnread,
        };
      })
      .filter((row) => row.otherUid)
      .sort(
        (a, b) =>
          Number(b.lastMessageAt || b.updatedAt || 0) -
          Number(a.lastMessageAt || a.updatedAt || 0),
      );

    onChange(rows);
  };

  const ensureMessageListener = (dmId: string) => {
    if (messageUnsubs.has(dmId)) return;

    // Last 100 messages is plenty for the compact unread badge and bounds the
    // listener cost. UI displays 99+ if the count ever reaches that range.
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'crewDMs', dmId, 'messages'),
        orderBy('createdAt', 'desc'),
        limit(100),
      ),
      (snapshot) => {
        if (cancelled) return;

        messageMetaByDm.set(
          dmId,
          snapshot.docs.map((messageDoc) => {
            const data = messageDoc.data() as any;
            return {
              senderId: clean(data?.senderId),
              createdAt: toMillis(data?.createdAt, Number(data?.createdAt || 0)),
              status: clean(data?.status) || 'visible',
            };
          }),
        );
        emit();
      },
      (error) => {
        console.warn('[crewDMs] unread message count subscription failed', { dmId, error });
        messageMetaByDm.set(dmId, []);
        onError?.(error);
        emit();
      },
    );

    messageUnsubs.set(dmId, unsubscribe);
  };

  const ensureReadListener = (dmId: string) => {
    if (readUnsubs.has(dmId)) return;

    const unsubscribe = onSnapshot(
      doc(db, 'crewDMs', dmId, 'reads', currentUid),
      (snapshot) => {
        if (cancelled) return;
        readAtByDm.set(
          dmId,
          snapshot.exists()
            ? toMillis(
                (snapshot.data() as any)?.lastReadAt,
                Number((snapshot.data() as any)?.lastReadAt || 0),
              )
            : 0,
        );
        emit();
      },
      (error) => {
        // A missing thread/read receipt is normal before the first message.
        // Any actual permission error is still surfaced for diagnostics.
        console.warn('[crewDMs] read receipt subscription failed', { dmId, error });
        readAtByDm.set(dmId, 0);
        onError?.(error);
        emit();
      },
    );

    readUnsubs.set(dmId, unsubscribe);
  };

  const ensureViewListener = (dmId: string) => {
    if (viewUnsubs.has(dmId)) return;

    const unsubscribe = onSnapshot(
      doc(db, 'crewDMs', dmId, 'views', currentUid),
      (snapshot) => {
        if (cancelled) return;
        clearedAtByDm.set(
          dmId,
          snapshot.exists()
            ? toMillis(
                (snapshot.data() as any)?.clearedAt,
                Number((snapshot.data() as any)?.clearedAt || 0),
              )
            : 0,
        );
        emit();
      },
      (error) => {
        console.warn('[crewDMs] view-state subscription failed', { dmId, error });
        clearedAtByDm.set(dmId, 0);
        onError?.(error);
        emit();
      },
    );

    viewUnsubs.set(dmId, unsubscribe);
  };

  // Important: use exact document listeners for accepted relationship IDs.
  // Firestore can authorize each GET against that relationship directly. A
  // collection query over participantIds requires query-rule proof across every
  // possible crewDM document and was producing permission-denied noise.
  desiredDmIds.forEach((dmId) => {
    const unsubscribe = onSnapshot(
      doc(db, 'crewDMs', dmId),
      (snapshot) => {
        if (cancelled) return;

        if (!snapshot.exists()) {
          threads.delete(dmId);
          const readUnsub = readUnsubs.get(dmId);
          if (readUnsub) {
            try { readUnsub(); } catch {}
            readUnsubs.delete(dmId);
          }
          const viewUnsub = viewUnsubs.get(dmId);
          if (viewUnsub) {
            try { viewUnsub(); } catch {}
            viewUnsubs.delete(dmId);
          }
          const messageUnsub = messageUnsubs.get(dmId);
          if (messageUnsub) {
            try { messageUnsub(); } catch {}
            messageUnsubs.delete(dmId);
          }
          readAtByDm.delete(dmId);
          clearedAtByDm.delete(dmId);
          messageMetaByDm.delete(dmId);
          emit();
          return;
        }

        const thread = normalizeThread(snapshot.id, snapshot.data());
        if (thread.participantIds.includes(currentUid)) {
          threads.set(dmId, thread);
          ensureReadListener(dmId);
          ensureViewListener(dmId);
          ensureMessageListener(dmId);
        } else {
          threads.delete(dmId);
        }
        emit();
      },
      (error) => {
        console.warn('[crewDMs] thread subscription failed', { dmId, error });
        threads.delete(dmId);
        onError?.(error);
        emit();
      },
    );

    threadUnsubs.set(dmId, unsubscribe);
  });

  return () => {
    cancelled = true;
    threadUnsubs.forEach((unsubscribe) => {
      try { unsubscribe(); } catch {}
    });
    readUnsubs.forEach((unsubscribe) => {
      try { unsubscribe(); } catch {}
    });
    viewUnsubs.forEach((unsubscribe) => {
      try { unsubscribe(); } catch {}
    });
    messageUnsubs.forEach((unsubscribe) => {
      try { unsubscribe(); } catch {}
    });
    threadUnsubs.clear();
    readUnsubs.clear();
    viewUnsubs.clear();
    messageUnsubs.clear();
    threads.clear();
    readAtByDm.clear();
    clearedAtByDm.clear();
    messageMetaByDm.clear();
  };
}

export function subscribeCrewDMViewState(
  uid: string,
  dmId: string,
  onChange: (state: CrewDMViewState) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const currentUid = clean(uid);
  const id = clean(dmId);

  if (!currentUid || !id) {
    onChange({ uid: currentUid, clearedAt: 0, updatedAt: 0 });
    return () => {};
  }

  return onSnapshot(
    doc(db, 'crewDMs', id, 'views', currentUid),
    (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() as any : {};
      onChange({
        uid: currentUid,
        clearedAt: toMillis(data?.clearedAt, Number(data?.clearedAt || 0)),
        updatedAt: toMillis(data?.updatedAt, Number(data?.updatedAt || 0)),
      });
    },
    (error) => {
      console.warn('[crewDMs] view-state subscription failed', { dmId: id, error });
      onError?.(error);
      onChange({ uid: currentUid, clearedAt: 0, updatedAt: 0 });
    },
  );
}

export async function clearCrewDMForUser(uid: string, dmId: string): Promise<void> {
  const currentUid = await ensureAuthed('clearCrewDMForUser');
  const requestedUid = clean(uid);
  const id = clean(dmId);

  if (!id || requestedUid !== currentUid) {
    throw new Error('Crew message identity mismatch.');
  }

  const threadRef = doc(db, 'crewDMs', id);
  const threadSnap = await getDoc(threadRef);
  if (!threadSnap.exists()) return;

  const thread = normalizeThread(threadSnap.id, threadSnap.data());
  if (!thread.participantIds.includes(currentUid)) {
    throw new Error('You do not have access to this Crew conversation.');
  }

  const now = nowMs();
  await setDoc(
    doc(threadRef, 'views', currentUid),
    {
      uid: currentUid,
      clearedAt: Math.max(now, Number(thread.lastMessageAt || 0)),
      updatedAt: now,
    },
    { merge: true },
  );

  await markCrewMessageNotificationsReadForThread(currentUid, id).catch(() => undefined);
}

export function subscribeCrewDMMessages(
  dmId: string,
  onChange: (messages: CrewDMMessage[]) => void,
  onError?: (error: unknown) => void,
  max = 150,
): Unsubscribe {
  const id = clean(dmId);
  if (!id) {
    onChange([]);
    return () => {};
  }

  const messagesQuery = query(
    collection(db, 'crewDMs', id, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(Math.max(1, Math.min(Number(max || 150), 250))),
  );

  return onSnapshot(
    messagesQuery,
    (snapshot) => {
      const rows = snapshot.docs
        .map((messageDoc) => normalizeMessage(messageDoc.id, messageDoc.data()))
        .sort((a, b) => a.createdAt - b.createdAt);
      onChange(rows);
    },
    (error) => {
      console.error('[crewDMs] message subscription failed', error);
      onError?.(error);
      onChange([]);
    },
  );
}

export async function sendCrewDMMessage(params: {
  currentUser: UserProfile;
  targetUid: string;
  text: string;
}): Promise<{ dmId: string; messageId: string }> {
  const senderUid = await ensureAuthed('sendCrewDMMessage');
  const profileUid = clean((params.currentUser as any)?.uid);
  if (profileUid && profileUid !== senderUid) throw new Error('Crew message identity mismatch.');

  const targetUid = clean(params.targetUid);
  const text = clean(params.text).slice(0, 2000);
  if (!targetUid || targetUid === senderUid) throw new Error('Choose a crewmate to message.');
  if (!text) throw new Error('Write a message first.');

  const { dmId, participantIds } = await requireAcceptedRelationship(senderUid, targetUid);
  const threadRef = doc(db, 'crewDMs', dmId);
  const messageRef = doc(collection(threadRef, 'messages'));
  const now = nowMs();

  const existing = await getDoc(threadRef);
  const batch = writeBatch(db);

  if (!existing.exists()) {
    batch.set(threadRef, {
      relationshipId: dmId,
      participantIds,
      createdBy: senderUid,
      createdAt: now,
      updatedAt: now,
      lastMessageText: text,
      lastMessageSenderId: senderUid,
      lastMessageAt: now,
      status: 'active',
    });
  } else {
    batch.update(threadRef, {
      updatedAt: now,
      lastMessageText: text,
      lastMessageSenderId: senderUid,
      lastMessageAt: now,
      status: 'active',
    });
  }

  batch.set(messageRef, {
    dmId,
    senderId: senderUid,
    text,
    createdAt: now,
    updatedAt: now,
    status: 'visible',
  });

  batch.set(
    doc(threadRef, 'reads', senderUid),
    { uid: senderUid, lastReadAt: now, updatedAt: now },
    { merge: true },
  );

  await batch.commit();

  const actorName =
    clean((params.currentUser as any)?.displayName) ||
    clean((params.currentUser as any)?.name) ||
    clean((params.currentUser as any)?.username) ||
    'A crewmate';
  const actorAvatar =
    clean((params.currentUser as any)?.avatar) ||
    clean((params.currentUser as any)?.avatarUrl) ||
    clean((params.currentUser as any)?.photoURL) ||
    '';

  void createNotification({
    userId: targetUid,
    id: `crew_dm_${dmId}_${messageRef.id}`,
    type: NotificationType.CREW_MESSAGE,
    title: 'Crew message',
    body: `${actorName}: ${text.slice(0, 120)}`,
    preview: text.slice(0, 120),
    link: { view: View.Crew, targetId: dmId, subTargetId: senderUid },
    targetId: dmId,
    subTargetId: senderUid,
    actor: { uid: senderUid, name: actorName, avatar: actorAvatar },
    entity: {
      userId: senderUid,
      dmId,
      messageId: messageRef.id,
    },
    read: false,
  }).catch((error) => console.warn('[crewDMs] message notification skipped', error));

  return { dmId, messageId: messageRef.id };
}

export async function markCrewDMRead(uid: string, dmId: string): Promise<void> {
  const currentUid = await ensureAuthed('markCrewDMRead');
  const requestedUid = clean(uid);
  const id = clean(dmId);
  if (!id || requestedUid !== currentUid) return;

  const threadSnap = await getDoc(doc(db, 'crewDMs', id));
  if (!threadSnap.exists()) return;
  const thread = normalizeThread(threadSnap.id, threadSnap.data());
  if (!thread.participantIds.includes(currentUid)) return;

  const readAt = Math.max(nowMs(), Number(thread.lastMessageAt || 0));
  await setDoc(
    doc(db, 'crewDMs', id, 'reads', currentUid),
    { uid: currentUid, lastReadAt: readAt, updatedAt: nowMs() },
    { merge: true },
  );

  await markCrewMessageNotificationsReadForThread(currentUid, id).catch(() => undefined);
}

export async function updateCrewDMMessage(
  dmId: string,
  messageId: string,
  textInput: string,
): Promise<void> {
  const uid = await ensureAuthed('updateCrewDMMessage');
  const id = clean(dmId);
  const mid = clean(messageId);
  const text = clean(textInput).slice(0, 2000);
  if (!id || !mid || !text) throw new Error('Message text is required.');

  const ref = doc(db, 'crewDMs', id, 'messages', mid);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Message not found.');
  const message = normalizeMessage(snap.id, snap.data());
  if (message.senderId !== uid) throw new Error('You can only edit your own messages.');
  if (message.status === 'deleted') throw new Error('Deleted messages cannot be edited.');

  const now = nowMs();
  await updateDoc(ref, { text, editedAt: now, updatedAt: now });

  const threadRef = doc(db, 'crewDMs', id);
  const threadSnap = await getDoc(threadRef);
  if (
    threadSnap.exists() &&
    clean((threadSnap.data() as any)?.lastMessageSenderId) === uid &&
    Number((threadSnap.data() as any)?.lastMessageAt || 0) === message.createdAt
  ) {
    await updateDoc(threadRef, { lastMessageText: text, updatedAt: now }).catch(() => undefined);
  }
}

export async function deleteCrewDMMessage(dmId: string, messageId: string): Promise<void> {
  const uid = await ensureAuthed('deleteCrewDMMessage');
  const id = clean(dmId);
  const mid = clean(messageId);
  if (!id || !mid) return;

  const ref = doc(db, 'crewDMs', id, 'messages', mid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const message = normalizeMessage(snap.id, snap.data());
  if (message.senderId !== uid) throw new Error('You can only remove your own messages.');

  const now = nowMs();
  await updateDoc(ref, {
    text: '',
    status: 'deleted',
    deletedAt: now,
    updatedAt: now,
  });

  const threadRef = doc(db, 'crewDMs', id);
  const threadSnap = await getDoc(threadRef);
  if (
    threadSnap.exists() &&
    clean((threadSnap.data() as any)?.lastMessageSenderId) === uid &&
    Number((threadSnap.data() as any)?.lastMessageAt || 0) === message.createdAt
  ) {
    await updateDoc(threadRef, { lastMessageText: 'Message removed', updatedAt: now }).catch(() => undefined);
  }
}
